import { Helmet } from 'react-helmet-async';
import { isPathMode } from '../config/markets';

const SITE_NAME = 'Off the Clock Spots';
const BASE_URL = 'https://www.offtheclockspots.com';
const DEFAULT_DESCRIPTION = 'Find the best happy hour deals, daily specials, trivia, bingo, live music and local events in Naperville, Aurora, and surrounding communities.';
const OG_IMAGE = `${BASE_URL}/images/og-image.png`;

/**
 * SEOHead — sets <title>, meta description, Open Graph, Twitter Card, and canonical URL.
 *
 * Props:
 *   title       – Page title (will be appended with " | Off the Clock Spots")
 *   description – Meta description (falls back to DEFAULT_DESCRIPTION)
 *   path        – URL path for canonical (e.g. '/naperville'). Omit for root.
 *   noindex     – If true, add robots noindex,nofollow (for admin/login pages)
 *   market      – Market config object (optional, used for market-specific OG)
 */
export default function SEOHead({ title, description, path, noindex, market }) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Happy Hours, Daily Specials & Events`;
  const desc = description || DEFAULT_DESCRIPTION;
  const canonicalPath = path || '';
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;

  // In path-mode, use the market slug for canonical
  // In env-mode, use the path as-is
  const effectiveCanonical = isPathMode() && market?.marketSlug && !canonicalPath
    ? `${BASE_URL}/${market.marketSlug}`
    : canonicalUrl;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={effectiveCanonical} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={effectiveCanonical} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* noindex for admin/login pages */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
}
